import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import { useQuery } from '@tanstack/react-query';

import { useCurrentUser } from '@/react/hooks/useUser';
import {
  Option,
  PortainerSelect,
} from '@/react/components/form-components/PortainerSelect';
import { Chart } from '@/react/kubernetes/helm/types';
import {
  ChartVersion,
  getSearchHelmRepo,
} from '@/react/kubernetes/helm/helmChartSourceQueries/useHelmRepoVersions';
import { HelmVersionSelect } from '@/react/kubernetes/helm/components/HelmVersionSelect';
import { HelmValuesInput } from '@/react/kubernetes/helm/components/HelmValuesInput';
import { HelmTemplatesSelect } from '@/react/kubernetes/helm/components/HelmTemplatesSelect';
import {
  useUserHelmRepositories,
  flattenHelmRegistries,
} from '@/react/kubernetes/helm/helmChartSourceQueries/useHelmRegistries';
import { useHelmChartList } from '@/react/kubernetes/helm/helmChartSourceQueries/useHelmChartList';
import { useHelmChartValues } from '@/react/kubernetes/helm/helmChartSourceQueries/useHelmChartValues';

import { FormSection } from '@@/form-components/FormSection';
import { FormControl } from '@@/form-components/FormControl';

import { FormValues } from '../types';

import { DeploymentSection } from './DeploymentSection';
import { HelmInstallOptionsSection } from './HelmInstallOptionsSection';

export function HelmRepositoryForm() {
  const { user } = useCurrentUser();
  const { values, setFieldValue, setValues, errors } =
    useFormikContext<FormValues>();

  const [selectedChart, setSelectedChart] = useState<Chart | null>(null);
  // Track the selected repo URL (source dropdown value)
  const [selectedRepoUrl, setSelectedRepoUrl] = useState<string | null>(null);

  // Auto-generate release name from stack name
  useEffect(() => {
    if (values.name) {
      setValues((prev) => ({
        ...prev,
        helmReleaseName: `edge-${values.name}`,
      }));
    }
  }, [values.name, setValues]);

  // Build flat list of user repos for the source dropdown
  const repoOptionsQuery = useUserHelmRepositories({
    select: (registries) =>
      flattenHelmRegistries(registries).map<Option<string>>((url) => ({
        label: url,
        value: url,
      })),
  });

  // Fetch chart list from the selected HTTP repo
  const chartListQuery = useHelmChartList(
    user.Id,
    0,
    { url: selectedRepoUrl ?? undefined },
    false // isOCI = false, HTTP repos only
  );

  // Fetch chart versions directly (avoids useHelmRepoVersions which calls useEnvironmentId)
  const versionOptionsQuery = useQuery<
    ChartVersion[],
    unknown,
    Option<ChartVersion>[]
  >({
    queryKey: ['helm', 'versions', selectedChart?.name, selectedChart?.repo],
    queryFn: () =>
      getSearchHelmRepo({
        chart: selectedChart!.name,
        repo: selectedChart!.repo,
      }),
    enabled: !!selectedChart,
    staleTime: 60 * 60 * 1000,
    select: (versions) =>
      versions.map((v, index) => ({
        label: index === 0 ? `${v.version} (latest)` : v.version,
        value: v,
      })),
  });

  // Auto-select latest version when versions load
  const prevVersionsLengthRef = useRef(0);
  useEffect(() => {
    const currentLength = versionOptionsQuery.data?.length ?? 0;
    if (prevVersionsLengthRef.current === 0 && currentLength > 0) {
      const latest = versionOptionsQuery.data?.[0]?.value?.version;
      if (latest) {
        setFieldValue('helmChartVersion', latest);
      }
    }
    prevVersionsLengthRef.current = currentLength;
  }, [versionOptionsQuery.data, setFieldValue]);

  const selectedVersion = useMemo(
    () =>
      versionOptionsQuery.data?.find(
        (v) => v.value?.version === values.helmChartVersion
      )?.value ??
      versionOptionsQuery.data?.[0]?.value ??
      null,
    [versionOptionsQuery.data, values.helmChartVersion]
  );

  const isLatestVersionFetched =
    !versionOptionsQuery.data?.length ||
    selectedVersion?.version === versionOptionsQuery.data?.[0]?.value?.version;

  // Fetch chart default values for the reference pane
  const chartValuesRefQuery = useHelmChartValues(
    {
      chart: selectedChart?.name ?? '',
      version: values.helmChartVersion,
      repo: selectedChart?.repo,
    },
    isLatestVersionFetched
  );

  // RegistryValue for HelmTemplatesSelect (HTTP repo, non-OCI)
  const selectedRegistryValue = selectedRepoUrl
    ? {
        registryUrl: selectedRepoUrl,
        isOCI: false as const,
        name: selectedRepoUrl,
      }
    : null;

  return (
    <>
      <DeploymentSection releaseName={values.helmReleaseName || ''} />

      <FormSection title="Helm chart">
        <FormControl
          label="Helm chart source"
          inputId="helm-chart-source"
          required
          tooltip="Select the Helm repository to browse charts from"
          errors={errors.helmRepoUrl}
        >
          <PortainerSelect<string>
            inputId="helm-chart-source"
            placeholder="Select a repository"
            value={selectedRepoUrl ?? ''}
            options={repoOptionsQuery.data ?? []}
            isLoading={repoOptionsQuery.isInitialLoading}
            onChange={handleRegistryChange}
            isClearable
            data-cy="helm-chart-source-select"
            noOptionsMessage={() =>
              'No repositories found. Add one in User Settings → Helm repositories.'
            }
          />
        </FormControl>

        <HelmTemplatesSelect
          selectedChart={selectedChart}
          onSelect={handleChartSelect}
          isLoadingCharts={chartListQuery.isInitialLoading}
          charts={chartListQuery.data ?? []}
          selectedRegistry={selectedRegistryValue}
          isRegistryError={chartListQuery.isError}
          isOCI={false}
          formError={errors.helmChartName}
        />
      </FormSection>

      {selectedChart && (
        <FormSection title="Install options" className="mt-4">
          <FormControl label="Version" inputId="helm-version-input">
            <HelmVersionSelect
              value={selectedVersion}
              options={versionOptionsQuery.data ?? []}
              onChange={(v) =>
                setFieldValue('helmChartVersion', v?.version ?? '')
              }
              data-cy="helm-version-input"
              isLoading={versionOptionsQuery.isInitialLoading}
              isChartSelected
            />
          </FormControl>
          <HelmValuesInput
            values={values.helmValuesInline}
            setValues={(v) => setFieldValue('helmValuesInline', v)}
            valuesRef={chartValuesRefQuery.data?.values ?? ''}
            isValuesRefLoading={chartValuesRefQuery.isInitialLoading}
          />
        </FormSection>
      )}

      <HelmInstallOptionsSection />
    </>
  );

  function handleRegistryChange(repoUrl: string | null) {
    setSelectedRepoUrl(repoUrl);
    setSelectedChart(null);
    setValues((prev) => ({
      ...prev,
      helmRepoUrl: repoUrl ?? '',
      helmChartName: '',
      helmChartVersion: '',
      helmValuesInline: '',
    }));
  }

  function handleChartSelect(chart: Chart | null) {
    setSelectedChart(chart);
    setValues((prev) => ({
      ...prev,
      helmChartName: chart?.name ?? '',
      helmRepoUrl: chart ? chart.repo : prev.helmRepoUrl,
      helmChartVersion: chart?.versions[0] ?? '',
      helmValuesInline: chart ? prev.helmValuesInline : '',
    }));
  }
}
